import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { buildLegacyI18n, validationLocalization } from '@bingads-webui/build-legacy-i18n';
import classNames from 'classnames';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import ablEnforcementLegacyStrings from '@bingads-webui-cc-react/abl-enforcement-legacy-strings';
import ablLegacyStrings from '@bingads-webui-cc-react/address-tax-form-legacy-strings';
import pidlLegacyString from '@bingads-webui-cc-react/pidl-payment-method-actions-base-legacy-strings';
import entityUtilLegacyString from '@bingads-webui-clientcenter/entity-utils-legacy-strings';
import termAndConditionsLegacyString from '@bingads-webui-cc-react/terms-and-conditions-legacy-strings';
import autoPbcLightboxLegacyStrings from '@bingads-webui-cc-react/auto-pbc-lightbox-legacy-strings';
import offlinePiLegacyStrings from '@bingads-webui-cc-react/check-bank-transfer-legacy-strings';
import addFundsLegacyString from '@bingads-webui-cc-react/signup-add-funds-legacy-strings';
import { aggregateStrings } from '@bingads-webui-cc-react/account-business-location-legacy-strings';
import { fileDraggerLegacyString } from '@bingads-webui-campaign-react/file-dragger';
import { msSupportChatLocalization, requestCallModalLocalization } from '@bingads-webui-react/bingads-backbone-header-strings';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getInstrumentation, getInstrumentationService, InstrumentationWithServiceProvider } from '@bingads-webui-campaign-react/pmax-lite-instrumentation';
import { ServerContext, ServerContextWithActionProvider, withServerContext } from '@bingads-webui-campaign-react/server-context';
import { imageExperienceLegacyStrings } from './image-experience-legacy-strings';
import { OverreactDataFetcher } from './overreact-fetcher';

const getStyles = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    '& .fui-FluentProvider': {
      overflow: 'auto',
      flex: 1,
    },
    '& .collapse-content .fui-FluentProvider': {
      overflow: 'visible',
    },
  },
  '@global': {
    '.fui-Toaster': {
      bottom: '100px !important',
      left: '60px !important',
    },
  },
});

const SDKPostSignUpContextInteranal = React.memo(({
  serverContext,
  subSystemName,
  perfMarkerName,
  children,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [
    ValidationStringMap,
    validationLocalization,
    ablEnforcementLegacyStrings,
    autoPbcLightboxLegacyStrings,
    ablLegacyStrings,
    offlinePiLegacyStrings,
    pidlLegacyString,
    entityUtilLegacyString,
    termAndConditionsLegacyString,
    addFundsLegacyString,
    msSupportChatLocalization,
    requestCallModalLocalization,
    aggregateStrings,
    fileDraggerLegacyString,
    imageExperienceLegacyStrings,
  ]), [i18n]);
  const finalServerContext = useMemo(() => ({
    ...serverContext,
    CCBaseUrl: serverContext.Urls.ClientCenterBaseUrl,
  }), [serverContext]);

  const instrumentation = useMemo(() => getInstrumentation({
    serverContext,
    devConfig: {},
    subSystemName,
  }), [serverContext, subSystemName]);

  const instrumentationService = useMemo(() => getInstrumentationService({
    instrumentation,
    serverContext,
  }), [instrumentation, serverContext]);

  const styles = useDefaultStyles(getStyles);

  return (
    <div className={classNames(styles.container, 'sdk-page-container')}>
      <FluentProvider theme={webLightTheme}>
        <OverreactDataFetcher>
          <LegacyI18nContext.Provider value={legacyI18n}>
            <ServerContextWithActionProvider serverContext={finalServerContext}>
              <InstrumentationWithServiceProvider context={instrumentationService}>
                <ServerContext.Provider value={finalServerContext}>
                  <PerformanceMarkerProvider
                    name={perfMarkerName}
                    instrumentation={instrumentation}
                    immediateDone={false}
                  >
                    {children}
                  </PerformanceMarkerProvider>
                </ServerContext.Provider>
              </InstrumentationWithServiceProvider>
            </ServerContextWithActionProvider>
          </LegacyI18nContext.Provider>
        </OverreactDataFetcher>
      </FluentProvider>
    </div>
  );
});

SDKPostSignUpContextInteranal.propTypes = {
  serverContext: PropTypes.shape({
    Urls: PropTypes.shape({
      ClientCenterBaseUrl: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  subSystemName: PropTypes.string,
  perfMarkerName: PropTypes.string,
  children: PropTypes.element.isRequired,
};

SDKPostSignUpContextInteranal.defaultProps = {
  subSystemName: 'sdk-campaign-overview',
  perfMarkerName: 'MSAdsSDK',
};

export const SDKPostSignUpContext = withServerContext(SDKPostSignUpContextInteranal);
